/**
 * Reusable carousel component since Material doesn't have one yet.
 * Based on design defined in https://gxperts.atlassian.net/browse/BLS-6562
 * https://github.com/angular/components/issues/24649
 *
 * Usage notes:
 *
 * Make sure every slide has the same dimensions since the design assumes there will be only 1 slide
 * shown at a time!
 *
 * Implementation:
 *
 * <app-carousel>
 *   <app-slide>
 *     <image src="..." alt="slide-1" class="w-[300px] h-[300px]/>
 *   </app-slide>
 *  <app-slide>
 *     <image src="..." alt="slide-2" class="w-[300px] h-[300px]/>
 *   </app-slide>
 *  <app-slide>
 *     <image src="..." alt="slide-3" class="w-[300px] h-[300px]/>
 *   </app-slide>
 *   ...
 *   <app-slide>
 *     <component-xy class="w-[300px] h-[300px]/>
 *   </app-slide>
 * </app-carousel>
 *
 */

import { AfterViewInit, ChangeDetectionStrategy, Component, ContentChildren, ElementRef, Input, OnChanges, OnDestroy, QueryList, ViewChild } from '@angular/core';
import { SlideComponent } from './slide/slide.component';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { BehaviorSubject, combineLatestWith, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LetDirective } from '@ngrx/component';
import { TypedSimpleChanges } from '../../../utils/typescript';
import { isNumber } from '../../../core/utilities';

@Component({
  selector: 'app-carousel',
  standalone: true,
  imports: [
    MatIcon,
    NgClass,
    LetDirective,
  ],
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarouselComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() insetControls: boolean = false;
  @Input() activeSlideIndex: number = 0;
  @ViewChild('slideContainer') public readonly slideContainer: ElementRef;
  @ContentChildren(SlideComponent, {read: ElementRef}) public readonly slides: QueryList<ElementRef>;

  private resizeObserver: ResizeObserver;

  private readonly containerWidth = new BehaviorSubject<number>(null);
  private readonly containerHeight = new BehaviorSubject<number>(null);
  private readonly activeSlideIndexSubject = new BehaviorSubject<number>(this.activeSlideIndex);
  private readonly componentDestroyed = new Subject<void>();

  public readonly containerWidth$ = this.containerWidth.asObservable();
  public readonly containerHeight$ = this.containerHeight.asObservable();
  public readonly activeSlideIndex$ = this.activeSlideIndexSubject.asObservable();
  public readonly viewModel$ = this.containerWidth$
      .pipe(
          combineLatestWith(this.containerHeight$, this.activeSlideIndex$),
          map(([containerWidth, containerHeight, activeSlideIndex]) =>
              ({containerWidth, containerHeight, activeSlideIndex}))
      );

  /**
   * Run after component's view initializes
   */
  ngAfterViewInit() {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.setContainerDimensions();
        this.scrollToSlide(this.activeSlideIndex, 'auto')
      });
    });

    this.resizeObserver = new ResizeObserver(() => {
      // Run on with every resize event
      this.setContainerDimensions();
      this.scrollToSlide(this.activeSlideIndexSubject.getValue(), 'auto');
    });
    this.resizeObserver.observe(document.documentElement);
  }

  /**
   * Run on input change
   * @param changes
   */
  ngOnChanges(changes: TypedSimpleChanges<CarouselComponent>) {
    if (isNumber(changes?.activeSlideIndex?.currentValue)) {
      this.activeSlideIndexSubject.next(changes?.activeSlideIndex?.currentValue);
      // scroll to image corresponding to activeSlideIndex whenever it changes;
      this.scrollToSlide(changes?.activeSlideIndex?.currentValue);
    }
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.resizeObserver.unobserve(document.documentElement);
  }

  /**
   * Scroll to the given slide.
   * @param slideIndex
   * @param scrollBehavior
   */
  scrollToSlide(slideIndex: number, scrollBehavior = 'smooth') {
    const offset = this.slides?.get(slideIndex)?.nativeElement?.offsetLeft;

    if (offset || offset === 0) {
      this.slideContainer.nativeElement.scrollTo({
        left: offset,
        behavior: scrollBehavior}); // animate scrolling
      this.activeSlideIndexSubject.next(slideIndex);
    } else {
      console.warn('Offset not found');
    }
  }

  /**
   * Scroll to the next slide.
   */
  nextSlide(e: MouseEvent) {
    e.stopPropagation();
    const nextIndex = this.activeSlideIndexSubject.getValue() + 1;

    if (nextIndex < this.slides.length) {
      this.scrollToSlide(nextIndex);
    }
  }

  /**
   * Scroll to the previous slide.
   */
  previousSlide(e: MouseEvent) {
    e.stopPropagation();
    const nextIndex = this.activeSlideIndexSubject.getValue() - 1;

    if (nextIndex >= 0) {
      this.scrollToSlide(nextIndex);
    }
  }

  /**
   * Make sure that only 1 slide can be shown at a time - the rest will overflow the container
   * @private
   */
  private setContainerDimensions() {
    this.containerWidth.next(this.slides.get(this.activeSlideIndexSubject.getValue()).nativeElement.offsetWidth);
    this.containerHeight.next(this.slides.get(this.activeSlideIndexSubject.getValue()).nativeElement.offsetHeight);
  }
}
